import React, { useMemo } from 'react';
import { useContentfulContext } from '../../../../contexts/ContentfulContext';
import AgGridWrapper, { getAgGridSortStorageKey } from '../../../UI/AgGrid';
import Card from '../../../UI/Card';

const TableView = ({ rowData, handleOnClickCallback, setGridApi }) => {
    const { enums, content } = useContentfulContext();
    
    const onGridReady = (params) => {
        setGridApi(params.api);

        const storageKey = getAgGridSortStorageKey("choose-template-table");
        const sortModel = JSON.parse(localStorage.getItem(storageKey));

        if (!sortModel || sortModel?.length === 0) {
            params.columnApi.applyColumnState({
                state: [    
                    { colId: 'last_edited', sort: 'desc' }
                ],
                applyOrder: false,
            });
        }
    };

    const autoSizeStrategy = useMemo(() => {
        return {
            type: "fitGridWidth",
            defaultMinWidth: 140,
            columnLimits: [
                {
                    colId: "template_name",
                    minWidth: 280,
                },
                {
                    colId: "type",
                    minWidth: 160,
                },
                {
                    colId: "scope",
                    minWidth: 140,
                },
                {
                    colId: "last_edited",
                    minWidth: 160,
                },
                {
                    colId: "managed_by",
                    minWidth: 160,
                }
            ],
        };
    }, []);

    const colDefs = [
        {
            headerName: content.CHOOSE_TEMPLATE_CONTENT.table_headers.template_name,
            field: "template_name",
            filter: true,
            cellRenderer: ({ data, rowIndex }) => {
                const disabledStyle = data?.isDisabled
                    ? 'text-stride-muted cursor-not-allowed opacity-50'
                    : 'text-primary hover:text-primary-hover hover:underline font-semibold cursor-pointer';
        
                const tooltipText = data?.isDisabled
                    ? content.CHOOSE_TEMPLATE_CONTENT.not_accessable
                    : "";
        
                return (
                    <div className="relative inline-block text-left">
                        <button
                            type="button"
                            className={`text-left block ${disabledStyle}`}
                            disabled={data?.isDisabled}
                            onClick={() => !data?.isDisabled && handleOnClickCallback(data)}
                            id={rowIndex == 0 ? "choose-first-template-id" : ""}
                            title={tooltipText}
                        >
                            {data.template_name}
                        </button>
                    </div>
                );
            },
            suppressMovable: true
        },
        {
            headerName: content.CHOOSE_TEMPLATE_CONTENT.table_headers.type,
            field: "type",
            cellRenderer: ({ data }) => {
                const typeName = enums?.templateTypes[data?.type]?.name || data?.type || "—";
                return (
                    <span className="stride-badge-pill bg-slate-100 text-slate-700 text-xs font-medium">
                        {typeName}
                    </span>
                );
            },
            sortable: true,
            suppressMovable: true
        },
        {
            headerName: content.CHOOSE_TEMPLATE_CONTENT.table_headers.scope,
            field: "scope",
            cellRenderer: ({ data }) => {
                const isGlobal = data.scope === 'global';
                return (
                    <span className={`stride-badge-pill text-xs font-semibold ${isGlobal ? 'bg-purple-50 text-purple-700 border border-purple-200' : 'bg-blue-50 text-blue-700 border border-blue-200'}`}>
                        {enums?.templateScopes[data.scope]?.name || data.scope || "—"}
                    </span>
                );
            },
            sortable: true,
            suppressMovable: true
        },
        {
            headerName: content.CHOOSE_TEMPLATE_CONTENT.table_headers.last_edited,
            field: "last_edited",
            cellRenderer: ({ data }) => (
                <span className="text-xs text-stride-muted tabular-nums">
                    {data?.last_edited || "—"}
                </span>
            ),
            comparator: (date1, date2) => new Date(date1) - new Date(date2),
            suppressMovable: true,
            sortable: true
        },
        {
            headerName: content.CHOOSE_TEMPLATE_CONTENT.table_headers.managed_by,
            field: "managed_by",
            cellRenderer: ({ data }) => {
                const val = data?.managed_by;
                const name = typeof val === 'object' ? val?.name || val?.email : val;
                return <span className="text-stride-body text-xs font-medium">{name || "—"}</span>;
            },
            sortable: false,
            resizable: false,
            suppressMovable: true
        },
    ];

    return (
        <Card variant="default" padding="none" className="overflow-hidden border border-border shadow-card">
            <div className="p-5">
                <AgGridWrapper
                    tableId="choose-template-table"
                    rowData={rowData}
                    columnDefs={colDefs}
                    autoSizeStrategy={autoSizeStrategy}
                    onGridReady={onGridReady}
                    suppressCellFocus={true}
                    domLayout="autoHeight"
                    getRowClass={(params) => {
                        return params.data?.isDisabled ? 'ag-row-disabled' : '';
                    }}
                />
            </div>
        </Card>
    );
};

export default TableView;
